import { Box, Typography, Tooltip, IconButton, Grid } from '@mui/material'
import React, { useCallback, useEffect, useState } from 'react'
import CasinoIcon from '@mui/icons-material/Casino'
import { ApiGetSpinConfigStatus } from '../../apiService/Core/Apicall'
import { toast } from 'react-toastify'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { useNavigate } from 'react-router-dom'


interface SpinConfigData {
    sequence: number[]
    position: number
    distribution: Record<string, number>
    updatedAt: string
}

const AMOUNT_COLORS: Record<number, { color: string; bg: string; border: string }> = {
    5: { color: '#8a8a8a', bg: '#8a8a8a12', border: '#8a8a8a35' },
    10: { color: '#3b82f6', bg: '#3b82f618', border: '#3b82f645' },
    25: { color: '#f59e0b', bg: '#f59e0b18', border: '#f59e0b55' },
    50: { color: '#10b981', bg: '#10b9811c', border: '#10b98165' },
}

const getAmountStyle = (amount: number) =>
    AMOUNT_COLORS[amount] || { color: '#9a9a9a', bg: '#9a9a9a10', border: '#9a9a9a30' }

const StatCard: React.FC<{ label: string; value: string; sub?: string; color: string; icon: React.ReactNode }> = ({ label, value, sub, color, icon }) => (
    <Box sx={{
        background: 'linear-gradient(135deg, #2a2a2a 0%, #222 100%)',
        border: '1px solid #333',
        borderRadius: '14px',
        p: 2.5,
        position: 'relative',
        overflow: 'hidden',
        transition: 'border-color 0.25s',
        '&:hover': { borderColor: color + '60' },
        '&::before': {
            content: '""',
            position: 'absolute',
            top: 0, left: 0, right: 0,
            height: '2px',
            background: `linear-gradient(90deg, ${color}00, ${color}, ${color}00)`,
        },
    }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <Typography fontSize={10.5} color="#797979" textTransform="uppercase" letterSpacing="0.12em" fontWeight={600}>{label}</Typography>
            <Box sx={{ color, opacity: 0.75 }}>{icon}</Box>
        </Box>
        <Typography fontSize={22} fontWeight={800} color={color} sx={{ letterSpacing: '-0.02em' }}>{value}</Typography>
        {sub && <Typography fontSize={11} color="#666" mt={0.5}>{sub}</Typography>}
    </Box>
)

const LegendDot: React.FC<{ amount: number }> = ({ amount }) => {
    const style = getAmountStyle(amount)
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
            <Box sx={{ width: 9, height: 9, borderRadius: '3px', background: style.color }} />
            <Typography fontSize={11.5} color="#8a8a8a" fontWeight={600}>${amount}</Typography>
        </Box>
    )
}

const SpinSequenceMonitor: React.FC = () => {
    const [data, setData] = useState<SpinConfigData | null>(null)
    const navigate = useNavigate()

    const fetchStatus = useCallback(async () => {
        try {
            const res = await ApiGetSpinConfigStatus()
            if (res?.status) {
                setData(res.data)
            }
        } catch (error: any) {
            toast.error(error?.response?.data?.message || 'Failed to fetch spin sequence status')
        }
    }, [])

    useEffect(() => {
        fetchStatus()
    }, [fetchStatus])

    if (!data) {
        return (
            <Box className="common-layout flex-column">
                <Typography color="#4a4a4a" textAlign="center" mt={8}>
                    No spin sequence found. Run the seed script/endpoint first.
                </Typography>
            </Box>
        )
    }

    const { sequence, position, distribution, updatedAt } = data
    const totalSlots = sequence.length
    const nextAmount = sequence[position]

    return (
        <div className="common-layout flex-column">
            <Box className="border-10 p-2 px-4 mixed-orange mb-4 both-circle d-flex align-items-center justify-content-between">
                <Box className="d-flex gap-3 align-items-center">
                    <IconButton onClick={() => navigate(-1)} size="small">
                        <ArrowBackIcon sx={{ color: '#fff' }} />
                    </IconButton>
                    <Typography className="fs-24 text-white fw-medium" sx={{ lineHeight: 1.1 }}>
                        Spin Sequence Monitor
                    </Typography>
                </Box>
            </Box>

            <Grid container spacing={2} mb={3}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatCard
                        label="Current Position"
                        value={`${position} / ${totalSlots - 1}`}
                        sub={`Next reward: $${nextAmount}`}
                        color="#f2674a"
                        icon={<CasinoIcon fontSize="small" />}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatCard
                        label="$5 Slots"
                        value={String(distribution['5'] ?? 0)}
                        sub={`${(((distribution['5'] ?? 0) / totalSlots) * 100).toFixed(0)}% of sequence`}
                        color="#8a8a8a"
                        icon={<CasinoIcon fontSize="small" />}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatCard
                        label="$25 Slots"
                        value={String(distribution['25'] ?? 0)}
                        sub={`${(((distribution['25'] ?? 0) / totalSlots) * 100).toFixed(0)}% of sequence`}
                        color="#f59e0b"
                        icon={<CasinoIcon fontSize="small" />}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatCard
                        label="$50 Slots"
                        value={String(distribution['50'] ?? 0)}
                        sub={`${(((distribution['50'] ?? 0) / totalSlots) * 100).toFixed(0)}% of sequence`}
                        color="#10b981"
                        icon={<CasinoIcon fontSize="small" />}
                    />
                </Grid>
            </Grid>

            <Box className="bg-dark-color border-10 p-3">
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5, flexWrap: 'wrap', gap: 1.5 }}>
                    <Box>
                        <Typography fontSize={15} color="#e8e8e8" fontWeight={700} sx={{ letterSpacing: '-0.01em' }}>
                            Spin Sequence
                        </Typography>
                        <Typography fontSize={11.5} color="#666" mt={0.3}>
                            Full 100-slot cycle · arrow marks the next slot to be redeemed
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
                        <LegendDot amount={5} />
                        <LegendDot amount={10} />
                        <LegendDot amount={25} />
                        <LegendDot amount={50} />
                    </Box>
                </Box>

                {/* progress line */}
                <Box sx={{ position: 'relative', height: 4, borderRadius: '4px', background: '#1a1a1a', my: 3, overflow: 'hidden' }}>
                    <Box sx={{
                        position: 'absolute', top: 0, left: 0, height: '100%',
                        width: `${(position / (totalSlots - 1)) * 100}%`,
                        background: 'linear-gradient(90deg, #f2674a80, #f2674a)',
                        transition: 'width 0.4s ease',
                        boxShadow: '0 0 10px #f2674a80',
                    }} />
                </Box>

                <Grid container columns={50} spacing={1.25} sx={{ pt: 3 }}>
                    {sequence.map((amount, index) => {
                        const isCurrent = index === position
                        const isPast = index < position
                        const style = getAmountStyle(amount)

                        return (
                            <Grid key={index} size={2}>
                                <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                    {isCurrent && (
                                        <Box sx={{
                                            position: 'absolute',
                                            top: -22,
                                            left: '50%',
                                            transform: 'translateX(-50%)',
                                            fontSize: 15,
                                            color: '#f2674a',
                                            animation: 'bounce 1s infinite',
                                            filter: 'drop-shadow(0 0 6px #f2674a)',
                                            zIndex: 2,
                                        }}>
                                            ▼
                                        </Box>
                                    )}

                                    <Tooltip title={`Slot ${index}${isCurrent ? ' — next up' : isPast ? ' — already redeemed' : ''}`} arrow>
                                        <Box sx={{
                                            width: '100%',
                                            aspectRatio: '1',
                                            borderRadius: '10px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: isCurrent ? 14 : 12,
                                            fontWeight: 800,
                                            color: style.color,
                                            background: isCurrent
                                                ? `linear-gradient(135deg, ${style.bg}, ${style.border})`
                                                : style.bg,
                                            border: isCurrent ? `2px solid ${style.color}` : `1px solid ${style.border}`,
                                            boxShadow: isCurrent ? `0 0 16px ${style.color}70` : 'none',
                                            opacity: isPast ? 0.35 : 1,
                                            transform: isCurrent ? 'scale(1.15)' : 'none',
                                            transition: 'all 0.25s ease',
                                            cursor: 'default',
                                            '&:hover': {
                                                transform: isCurrent ? 'scale(1.15)' : 'scale(1.08)',
                                                opacity: 1,
                                            },
                                        }}>
                                            ${amount}
                                        </Box>
                                    </Tooltip>
                                </Box>
                            </Grid>
                        )
                    })}
                </Grid>
            </Box>

            <Typography fontSize={14} color="#ffffff" mt={2} textAlign="right">
                Last updated: {new Date(updatedAt).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
            </Typography>

            <style>{`
                @keyframes bounce {
                    0%, 100% { transform: translateX(-50%) translateY(0); }
                    50% { transform: translateX(-50%) translateY(4px); }
                }
            `}</style>
        </div>
    )
}

export default SpinSequenceMonitor